import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  MessageSquare,
  Plus,
  Settings2Icon,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { useServiceOrders } from "@features/cx/shared/hooks/useServiceOrder";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useNotes } from "@features/cx/shared/hooks/useNotes";
import {
  useChannelTemplates,
  useChannelType,
  useSendCommunication,
} from "@features/cx/consumerManagement/hooks";
import {
  SERVICE_REQUEST_STATUS,
  SERVICE_REQUEST_STATUS_DISPLAY,
} from "../utils/constants";
import { CommunicationDialog } from "./CommunicationDialog";
import { NoteDialog } from "./NoteDialog";
import { ServiceOrderDialogue } from "./ServiceOrderDialogue";
import { ActionButton } from "../types/consumerRequest";

interface RequestQuickActionsProps {
  request: {
    id: string;
    requestNo?: string;
    customer?: {
      id: string;
      firstName: string;
      lastName: string;
      phone: string;
      email: string;
      consumerNo: string;
    };
  };
  currentStatus: string;
  actionButtons?: ActionButton[];
  handleResolveRequest?: () => void;
  handleRejectRequest?: () => void;
}

export function RequestQuickActions({
  request,
  currentStatus,
  actionButtons = [],
  handleResolveRequest,
  handleRejectRequest,
}: RequestQuickActionsProps) {
  const remoteUtilityId = useRemoteUtilityId();
  const location = useLocation();
  const [showCommunicationDialog, setShowCommunicationDialog] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showServiceOrderDialog, setShowServiceOrderDialog] = useState(false);

  const isFromHistory = location.pathname.includes("/history/");

  const { serviceOrders } = useServiceOrders(
    {
      remote_utility_id: getRemoteUtilityId(),
      request_type: "service-order",
      request_id: request?.id,
    },
    { enabled: !!request?.id },
  );

  const { createNote } = useNotes({
    remoteUtilityId: Number(remoteUtilityId),
    requestId: Number(request.id),
  });

  const { mutateAsync: createCommunication } = useSendCommunication(
    request.id!,
  );

  const createNoteWrapper = (itemId: string) => ({
    mutateAsync: async (data: any) => {
      await createNote({
        source: "consumer_support_request",
        request_id: Number(itemId),
        remote_utility_id: Number(remoteUtilityId),
        notes: {
          note: data.notes.note,
          note_type: data.notes.noteType,
        },
      });
    },
  });

  const createCommunicationWrapper = (consumerId: string) => ({
    mutateAsync: (data: any) => createCommunication(data),
  });

  const channelTypeWrapper = () => useChannelType();
  const channelTemplatesWrapper = (channelType: string) =>
    useChannelTemplates(channelType);

  const hasApprovedOrInProgressSOP =
    serviceOrders?.some((serviceOrder) =>
      [
        "Created",
        "Not Assigned",
        "In-Progress",
        "Assigned",
        "Overdue",
        "Completed",
        "Accepted",
        "Refused",
        "Incomplete",
      ].includes(serviceOrder?.statusDisplay),
    ) || false;

  const quickActionButtons: ActionButton[] = [
    {
      id: "send-message",
      label: "Send Response to Consumer",
      icon: MessageSquare,
      variant: "outline",
      className: "w-full bg-blue-500 text-white hover:bg-blue-600",
      onClick: () => setShowCommunicationDialog(true),
    },
    {
      id: "add-service-order",
      label: "Create Service Order",
      icon: Settings2Icon,
      variant: "outline",
      disabled: currentStatus === "rejected",
      className: `w-full bg-purple-500 text-white hover:bg-purple-600 ${
        currentStatus === "rejected" ? "cursor-not-allowed disable" : ""
      }`,
      onClick: () => setShowServiceOrderDialog(true),
      condition: (status) => {
        return (
          status !==
          SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.COMPLETED]
        );
      },
    },
    {
      id: "add-internal-note",
      label: "Add Internal Note",
      icon: Plus,
      variant: "outline",
      className: "w-full bg-white text-black hover:bg-gray-100",
      onClick: () => setShowNotesDialog(true),
    },
    ...(!isFromHistory
      ? [
          {
            id: "resolve-request",
            label: "Resolve Request",
            icon: CheckCircle,
            variant: "outline" as const,
            className: "w-full bg-green-500 text-white hover:bg-green-600",
            onClick: handleResolveRequest,
            disabled: hasApprovedOrInProgressSOP,
            condition: (status: any) => {
              return (
                status !==
                  SERVICE_REQUEST_STATUS_DISPLAY[
                    SERVICE_REQUEST_STATUS.COMPLETED
                  ] &&
                status !==
                  SERVICE_REQUEST_STATUS_DISPLAY[
                    SERVICE_REQUEST_STATUS.REJECTED
                  ]
              );
            },
          },
          {
            id: "reject-request",
            label: "Reject Request",
            icon: XCircle,
            variant: "outline" as const,
            className: "w-full bg-red-500 hover:bg-red-600",
            onClick: handleRejectRequest,
            disabled: hasApprovedOrInProgressSOP,
            condition: (status: any) => {
              return (
                status !==
                  SERVICE_REQUEST_STATUS_DISPLAY[
                    SERVICE_REQUEST_STATUS.COMPLETED
                  ] &&
                status !==
                  SERVICE_REQUEST_STATUS_DISPLAY[
                    SERVICE_REQUEST_STATUS.REJECTED
                  ]
              );
            },
          },
        ]
      : []),
  ];

  const relevantActionButtons = actionButtons.filter(
    (action) => !action.condition || action.condition(currentStatus),
  );

  return (
    <div className="space-y-4 mt-4">
      {/* Primary Actions (Moved from Header) */}
      <div className="flex justify-end gap-2 min-h-[36px]">
        {relevantActionButtons.map((action) => (
          <Button
            key={action.id}
            size="sm"
            variant={action.variant}
            onClick={action.onClick}
            isLoading={action.isLoading}
            disabled={action.disabled}
          >
            <action.icon className="h-4 w-4 mr-2" />
            {action.label}
          </Button>
        ))}
      </div>

      {/* Quick Actions Card */}
      <Card>
        <CardContent className="">
          <h3 className="text-sm font-semibold  tracking-wider text-muted-foreground flex items-center gap-2">Quick Actions</h3>
          <div className="flex flex-col gap-2 mt-4">
            {quickActionButtons
              .filter(
                (action) =>
                  !action.condition || action.condition(currentStatus),
              )
              .map((action) => (
                <Button
                  key={action.id}
                  disabled={action.disabled}
                  size="sm"
                  variant={action.variant}
                  onClick={action.onClick}
                  isLoading={action.isLoading}
                  className="justify-start h-10 text-xs font-medium"
                >
                  <action.icon className="h-4 w-4 mr-2" />
                  {action.label}
                </Button>
              ))}
          </div>
        </CardContent>
      </Card>

      <NoteDialog
        isOpen={showNotesDialog}
        onClose={() => setShowNotesDialog(false)}
        itemId={request.id!}
        remoteUtilityId={Number(remoteUtilityId)}
        useSendNote={createNoteWrapper}
      />
      <CommunicationDialog
        isOpen={showCommunicationDialog}
        onClose={() => setShowCommunicationDialog(false)}
        consumerId={request.id!}
        email={request?.customer?.email || ""}
        phone={request?.customer?.phone || ""}
        remoteUtilityId={Number(remoteUtilityId)}
        communicationType="consumer_support_request"
        useChannelType={channelTypeWrapper}
        useChannelTemplates={channelTemplatesWrapper}
        useSendCommunication={createCommunicationWrapper}
      />
      <ServiceOrderDialogue
        consumerAccountNumber={request?.customer?.consumerNo || ""}
        consumerId={request?.customer?.id!}
        consumerName={
          request?.customer?.firstName + " " + request?.customer?.lastName
        }
        requestId={request.id!}
        remoteUtilityId={Number(remoteUtilityId)}
        isOpen={showServiceOrderDialog}
        onClose={() => setShowServiceOrderDialog(false)}
        onSuccess={() => setShowServiceOrderDialog(false)}
      />
    </div>
  );
}
